import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import { useSyncClient } from '~/hooks/models/useClient';
import { getSubscriptionStatus, PausePlan, SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import {
    openCancelSession,
    pauseSubscription,
    SubscriptionActionError,
} from '~/services/subscription/subscriptionActions';
import { readStoredClientID } from '~/utils/storedClientID';
import { formatList, monthlyPrice, unixDate } from '~/utils/pauseFormat';

import accountStyles from './Account.redesign.module.css';
import styles from './CancelFlow.module.css';
import { PauseStatusCard } from './PauseStatusCard';

function IconBack() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
    );
}
function IconPause() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1.2" />
            <rect x="14" y="5" width="4" height="14" rx="1.2" />
        </svg>
    );
}

/** « Vous profitez d'Unboared jusqu'au… / Aucun prélèvement le… / Reprise et prélèvement de… le… » */
function PlanDetails({
    plan,
    price,
    lang,
    cancelRemoved,
}: {
    plan: PausePlan;
    price: string;
    lang: string;
    cancelRemoved?: boolean;
}) {
    const { t } = useTranslation();
    const skipped = formatList(
        plan.skippedChargeDates.map((d) => unixDate(d, lang)),
        lang
    );
    return (
        <ul className={styles.details}>
            <li>{t('account.cancel.detailUntil', { date: unixDate(plan.startsAt, lang) })}</li>
            <li>{t('account.cancel.detailSkipped', { count: plan.skippedChargeDates.length, dates: skipped })}</li>
            <li className={styles.detailStrong}>
                {t('account.cancel.detailResume', { date: unixDate(plan.endsAt, lang), price })}
            </li>
            <li>{t('account.cancel.detailGamesBack', { date: unixDate(plan.resumesAt, lang) })}</li>
            {cancelRemoved && <li>{t('account.cancel.detailCancelsCancellation')}</li>}
        </ul>
    );
}

type Done = { plan: PausePlan; cancelRemoved: boolean };

/**
 * Écran « Plutôt faire une pause ? » (route /account/cancel), ouvert par
 * « Résilier » dans Mon compte. Propose une pause de 1, 2 ou 3 mois avec les
 * dates exactes (calculées par le serveur), ou « Continuer vers la résiliation »
 * → portail Stripe directement sur l'écran d'annulation (motifs Stripe).
 *
 * L'offre n'apparaît que si le serveur répond `canPause: true` : interrupteur
 * STRIPE_PAUSE=off, essai, impayé, déjà en pause… → pas d'offre.
 */
export function CancelFlow() {
    const { state } = useLocation();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [storedClientID] = useState<string>(readStoredClientID);
    const clientID: string = state?.clientID || storedClientID;
    const client = useSyncClient(clientID);
    const lang = i18n.language;

    const [sub, setSub] = useState<SubscriptionStatus | null | undefined>(undefined);
    const [version, setVersion] = useState(0);
    const [months, setMonths] = useState<1 | 2 | 3>(1);
    const [busy, setBusy] = useState<'pause' | 'cancel' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<Done | null>(null);
    const loggedOpen = useRef(false);
    const loggedOffer = useRef(false);

    useEffect(() => {
        if (!clientID) navigate('/home', { replace: true });
    }, [clientID, navigate]);

    useEffect(() => {
        if (!clientID) return undefined;
        let cancelled = false;
        // 1er chargement seulement : le serveur journalise l'ouverture de l'écran.
        const context = loggedOpen.current ? undefined : 'cancel_flow';
        loggedOpen.current = true;
        getSubscriptionStatus(clientID, { context }).then((s) => {
            if (!cancelled) setSub(s);
        });
        return () => {
            cancelled = true;
        };
    }, [clientID, version]);

    const options = sub?.canPause ? sub.pauseOptions || [] : [];
    const showOffer = !done && options.length > 0;

    useEffect(() => {
        if (showOffer && !loggedOffer.current) {
            loggedOffer.current = true;
            logEvent('pause_offer_view', 'CancelFlow', { clientID });
        }
    }, [showOffer, clientID]);

    const goToPortalCancel = async () => {
        logEvent('pause_offer_decline', 'CancelFlow', {
            clientID,
            offered: showOffer ? 1 : 0,
            reason: sub?.pauseReason || 'none',
        });
        setBusy('cancel');
        setError(null);
        try {
            const { url } = await openCancelSession(clientID, lang);
            window.location.assign(url);
        } catch {
            setBusy(null);
            setError(t('account.pause.error'));
        }
    };

    const confirmPause = async () => {
        const plan = options.find((o) => o.months === months);
        if (!plan) return;
        logEvent('pause_offer_select', 'CancelFlow', { clientID, months });
        setBusy('pause');
        setError(null);
        try {
            const result = await pauseSubscription(clientID, months, plan.startsAt);
            if (result.alreadyPaused || !result.plan) {
                // Une pause était déjà en place (double clic, autre onglet) : Mon compte la montre.
                navigate('/account?tab=subscription', { state: { clientID } });
                return;
            }
            // Dates réellement posées par le serveur (pas celles calculées à l'ouverture).
            setDone({ plan: result.plan, cancelRemoved: Boolean(result.cancelAtPeriodEndRemoved) });
        } catch (err) {
            const code = err instanceof SubscriptionActionError ? err.code : 'unknown';
            if (code === 'forbidden') setError(t('account.pause.adminOnly'));
            else if (code === 'period_changed' || code === 'operation_in_progress')
                setError(t('account.pause.phaseChanged'));
            else setError(t('account.pause.error'));
            // Éligibilité changée entre-temps (déjà en pause, interrupteur…) : on relit.
            if (code !== 'forbidden' && code !== 'network') setVersion((v) => v + 1);
        } finally {
            setBusy(null);
        }
    };

    const price = monthlyPrice(sub ?? null, lang);
    const selected = options.find((o) => o.months === months) || options[0];
    const stripeCustomerId: string | undefined = (client as any)?.stripeCustomerId;

    const continueButton = (label: string) => (
        <button type="button" className={styles.continue} onClick={goToPortalCancel} disabled={busy !== null}>
            {busy === 'cancel' ? t('account.cancel.redirecting') : label}
        </button>
    );

    let body: JSX.Element;
    if (done) {
        body = (
            <section className={accountStyles.card}>
                <div className={styles.doneIcon}>
                    <IconPause />
                </div>
                <h2 className={styles.h2}>{t('account.cancel.doneTitle')}</h2>
                <PlanDetails plan={done.plan} price={price} lang={lang} cancelRemoved={done.cancelRemoved} />
                <p className={styles.note}>{t('account.cancel.doneBody')}</p>
                <button
                    type="button"
                    className={accountStyles.saveBtn}
                    onClick={() => navigate('/account?tab=subscription', { state: { clientID } })}
                >
                    {t('account.cancel.doneBack')}
                </button>
            </section>
        );
    } else if (sub === undefined) {
        body = (
            <section className={accountStyles.card}>
                <p className={styles.note}>{t('account.cancel.loading')}</p>
            </section>
        );
    } else if (showOffer && selected) {
        body = (
            <section className={accountStyles.card}>
                <div className={styles.options} role="radiogroup" aria-label={t('account.cancel.title')}>
                    {options.map((o) => (
                        <button
                            type="button"
                            role="radio"
                            aria-checked={o.months === months}
                            key={o.months}
                            className={`${styles.option} ${o.months === months ? styles.optionOn : ''}`}
                            onClick={() => setMonths(o.months)}
                        >
                            <span className={styles.optionMonths}>
                                {t('account.pause.months', { count: o.months })}
                            </span>
                            <span className={styles.optionDate}>
                                {t('account.cancel.optionResume', { date: unixDate(o.endsAt, lang) })}
                            </span>
                        </button>
                    ))}
                </div>
                <PlanDetails plan={selected} price={price} lang={lang} cancelRemoved={sub?.cancelAtPeriodEnd} />
                <div className={styles.ctaRow}>
                    <button
                        type="button"
                        className={accountStyles.saveBtn}
                        onClick={confirmPause}
                        disabled={busy !== null}
                    >
                        <IconPause />
                        {t('account.cancel.pauseCta', { count: months })}
                    </button>
                </div>
                {error && <p className={styles.error}>{error}</p>}
                <div className={styles.footer}>{continueButton(t('account.cancel.continue'))}</div>
            </section>
        );
    } else {
        // Pas d'offre : on explique pourquoi quand c'est utile, puis la résiliation.
        const reason = sub?.pauseReason;
        let message: JSX.Element | null = null;
        if (sub?.pause?.status === 'paused') {
            message = (
                <>
                    <h2 className={styles.h2}>{t('account.cancel.alreadyPausedTitle')}</h2>
                    <PauseStatusCard
                        clientID={clientID}
                        sub={sub}
                        origin="cancel_flow"
                        onChanged={() => setVersion((v) => v + 1)}
                    />
                </>
            );
        } else if (reason === 'trialing' && sub?.trialEnd) {
            message = (
                <>
                    <h2 className={styles.h2}>{t('account.cancel.trialTitle')}</h2>
                    <p className={styles.note}>
                        {t('account.cancel.trialBody', { date: unixDate(sub.trialEnd, lang) })}
                    </p>
                </>
            );
        } else if (reason === 'payment_issue') {
            message = (
                <>
                    <h2 className={styles.h2}>{t('account.cancel.paymentIssueTitle')}</h2>
                    <p className={styles.note}>{t('account.cancel.paymentIssueBody')}</p>
                    {stripeCustomerId && (
                        <StripeServerForm
                            className={accountStyles.inlineForm}
                            route="/create-customer-portal-session"
                            clientId={clientID}
                            lang={lang}
                        >
                            <button type="submit" className={accountStyles.saveBtn}>
                                {t('account.pause.updateCard')}
                            </button>
                        </StripeServerForm>
                    )}
                </>
            );
        } else if (reason === 'recently_paused' && sub?.nextPauseEligibleAt) {
            message = (
                <p className={styles.note}>
                    {t('account.cancel.recentlyPaused', { date: unixDate(sub.nextPauseEligibleAt, lang) })}
                </p>
            );
        } else if (sub === null) {
            message = <p className={styles.note}>{t('account.cancel.loadError')}</p>;
        } else {
            message = <p className={styles.note}>{t('account.cancel.unavailable')}</p>;
        }
        body = (
            <section className={accountStyles.card}>
                {message}
                {error && <p className={styles.error}>{error}</p>}
                <div className={styles.footer}>{continueButton(t('account.cancel.continueOnly'))}</div>
            </section>
        );
    }

    return (
        <div className={accountStyles.page}>
            <div className={`${accountStyles.shell} ${styles.shell}`}>
                <header className={accountStyles.header}>
                    <div className={accountStyles.headTexts}>
                        <div className={accountStyles.kicker}>{t('account.cancel.kicker')}</div>
                        <h1 className={accountStyles.title}>
                            {done && t('account.pause.titleScheduled')}
                            {!done && (showOffer ? t('account.cancel.title') : t('account.cancelSubscription'))}
                        </h1>
                        {showOffer && <p className={styles.subtitle}>{t('account.cancel.subtitle')}</p>}
                    </div>
                    <div className={accountStyles.headRight}>
                        <button
                            type="button"
                            className={accountStyles.closeBtn}
                            onClick={() => navigate('/account?tab=subscription', { state: { clientID } })}
                        >
                            <IconBack />
                            {t('account.cancel.back')}
                        </button>
                    </div>
                </header>
                {body}
            </div>
        </div>
    );
}
